import parse from 'html-react-parser'
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next/pages'
import { routes } from 'utils/routes'
import styles from './Confirmation.module.scss'
import { useStoreSelector } from 'hooks/store'
import { CldImage } from 'next-cloudinary'
import { Heading, Img } from '@boltenergy-be/design-system'
import { REFERRAL_AMOUNT_GIVE, REFERRAL_AMOUNT_RECEIVE } from 'features/referral/Referral.constants'
import Link from 'components/link/Link'
import { BlocksContent } from '@strapi/blocks-react-renderer'
import BlocksRenderer from 'components/blocks-renderer/BlocksRenderer'
import { CustomerFlows, FlowAlternatives } from 'store/customer/enums'
import { SIGN_CHANNELS } from '@boltenergy-be/shared'
import { ConfirmationProps } from './types'
import IsFromPlentyFlowView from './components/IsFromPlentyFlowView'
import ConfirmationMailView from './components/ConfirmationMailView'
import ExtraAddressView from './components/ExtraAddressView'
import PlentyPropositionsView from './components/PlentyPropositionsView'
import ConfirmationWrapper from './components/ConfirmationWrapper'
import CreditCheckView from './components/CreditCheckView'

const Confirmation = ({ producer, activeAction }: ConfirmationProps) => {
  // Redux state
  const customer = useStoreSelector((store) => store.customer)
  const { flowAlternative, extraAddress, flows } = customer
  const { showPlentyPropositions } = customer.propositions
  const { creditCheckRequired } = flows[CustomerFlows.REGISTRATION]
  const isFromPlentyFlow = flowAlternative[FlowAlternatives.PLENTY]

  // Next router
  const router = useRouter()
  const { locale } = router

  // i18n
  const { t } = useTranslation('registration')

  // Order is important here, first true condition is rendered
  if (customer.inputs.overview.chosenSignChannel === SIGN_CHANNELS.confirmationMail) {
    return <ConfirmationMailView {...{ producer }} />
  }

  if (creditCheckRequired) {
    return <CreditCheckView />
  }

  if (isFromPlentyFlow) {
    return <IsFromPlentyFlowView {...{ producer }} />
  }

  if (extraAddress.isExtraAddressFlow) {
    return <ExtraAddressView {...{ producer }} />
  }

  if (showPlentyPropositions) {
    return <PlentyPropositionsView {...{ producer }} />
  }

  return (
    <ConfirmationWrapper>
      {/* PRODUCER CARD */}
      {producer && (
        <section className={styles['confirmation-card']}>
          <div className={styles.text}>
            <Heading as="h2" variant="h3" serif className="mb-400">
              {t(`confirmation.boltPropositions.title.confirmed`)}
            </Heading>

            <p className="mb-500">
              {t(`confirmation.boltPropositions.description`, {
                producerName: producer.name[locale]
              })}
            </p>

            <Link representation="button" href={routes(locale).home}>
              {t(`confirmation.boltPropositions.backToHome`)}
            </Link>
          </div>

          {producer.images.profile?.url && (
            <div className={styles.image}>
              <CldImage fill src={producer.images.profile?.url} alt={producer.name[locale]} />
            </div>
          )}
        </section>
      )}

      {/* REFERRAL CARD */}
      <section className={styles['confirmation-card']}>
        <div className={styles.image}>
          <Img
            publicId={activeAction?.image?.provider_metadata?.public_id || 'website/referral/elisabeth_juliet_wide'}
            fill
            objectPosition="top center"
            alt=""
          />
        </div>

        <div className={styles.text}>
          <Heading as="h2" variant="h3" serif className="mb-400">
            {activeAction?.shortContent?.title ||
              t(`confirmation.boltPropositions.referral.title`, {
                amountGive: REFERRAL_AMOUNT_GIVE,
                amountReceive: REFERRAL_AMOUNT_RECEIVE
              })}
          </Heading>

          {activeAction ? (
            <BlocksRenderer content={activeAction?.shortContent?.description as BlocksContent} />
          ) : (
            <p className={styles.description}>{parse(t(`confirmation.boltPropositions.referral.description`))}</p>
          )}

          <Link representation="button" href={routes(locale).referral} isFullwidthMobile className="mt-500">
            {t(`confirmation.boltPropositions.referral.button`)}
          </Link>
        </div>
      </section>
    </ConfirmationWrapper>
  )
}

export default Confirmation
